/* npm imports outside of vrbo */
import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {graphql} from 'react-apollo';
import {get, isEmpty} from 'lodash';
import Helmet, {HelmetProvider} from 'react-helmet-async';

/* Atoms (baseline) */
import {LoadingOverlay} from '../../atoms/baseline/baseline';

/* Atoms (custom) */
import {Typography} from '../../atoms/Typography/Typography';

/* Molecules (custom) */
import {AlertDangerWithState} from '../../molecules/AlertDangerWithState/AlertDangerWithState';
import InboxConversations from '../../molecules/InboxConversations/InboxConversations';
import Pagination from '../../molecules/Pagination/Pagination';

/* Queries and mutations */
import {InboxQuery} from './InboxQuery';
import {LoadingError} from '../../LoadingError/LoadingError';

/* Utils */
import haWrapper from '../../../utils/haWrapper';
import {inboxPresented, INBOX} from '../../../utils/edapUtils';
import scrollToAnimated from '../../../utils/scrollToAnimated';
import translate from '../../../utils/translations';
import updateADL from '../../../utils/adl';

export const Inbox = (props) => {
    const getStateFromProps = () => {
        const conversations = get(props, 'data.inbox.conversations', []);

        return {
            conversations,
            page: props.page || 1,
            results: conversations.length,
            totalResults: get(props, 'data.inbox.totalResults', 0),
        };
    };

    const [inboxState, setInboxState] = useState(getStateFromProps());
    const {conversations, errorMsg, page, results, totalResults} = inboxState;
    const {data, pageSize, systemData, translations, isVrbexNative} = props;
    const {inbox} = data;

    const setStateFromGql = () => {
        const inboxConversations = inbox.conversations;

        setInboxState({
            conversations: inboxConversations,
            page: inbox.page || 1,
            results: inboxConversations.length || 0,
            totalResults: inbox.totalResults || 0,
        });
    };

    useEffect(() => {
        updateADL('travelerdash inbox');

        // If SSR failed, retry
        if (!get(props, 'data.inbox', false)) {
            data.refetch();
        }

        if (inbox?.conversations && !data.loading && isEmpty(conversations)) {
            setStateFromGql();
        }

        haWrapper.userTiming.mark('primary.action.rendered');
        inboxPresented({actionLocation: INBOX});
    }, []);

    useEffect(() => {
        if (inbox?.conversations && !data.loading) {
            setStateFromGql();
        }
    }, [data, page, pageSize, translations]);

    const handlePageChange = (pageNumber) => {
        scrollToAnimated(0);

        props.data.refetch({page: pageNumber, pageSize});
    };

    const title = (
        <HelmetProvider>
            <Helmet>
                <title>
                    {translate(
                        translations,
                        'trav-dash/trav-home',
                        'nav',
                        'th.tabs.inbox'
                    )}
                </title>
            </Helmet>
        </HelmetProvider>
    );

    if (data.loading) {
        return (
            <div
                className="position-relative"
                data-testid="data-loading"
                style={{height: '300px'}}
            >
                {title}
                <LoadingOverlay loading />
            </div>
        );
    }

    if (data.error || isEmpty(inbox) || isEmpty(inbox.conversations)) {
        return (
            <div className="container" data-testid="data-error">
                {title}
                {!isVrbexNative && (
                    <Typography
                        className="margin-top-2x"
                        content={translate(
                            translations,
                            'trav-dash/trav-home',
                            'nav',
                            'th.tabs.inbox'
                        )}
                        TagName="h3"
                    />
                )}
                <LoadingError
                    emptyLabel={translate(
                        translations,
                        'trav-dash/trav-home',
                        'messages',
                        'th.inbox.empty.title'
                    )}
                    error={data.error}
                    systemData={systemData}
                    translations={translations}
                />
            </div>
        );
    }

    return (
        <div className="Inbox container">
            {title}
            {errorMsg && <AlertDangerWithState msg={errorMsg} />}
            {!isVrbexNative && (
                <Typography
                    className="margin-top-2x"
                    content={translate(
                        translations,
                        'trav-dash/trav-home',
                        'nav',
                        'th.tabs.inbox'
                    )}
                    TagName="h3"
                />
            )}
            <InboxConversations
                conversations={conversations}
                translations={translations}
                isVrbexNative={isVrbexNative}
            />
            {totalResults > pageSize && (
                <Pagination
                    handlePageChange={handlePageChange}
                    page={page}
                    pageSize={pageSize}
                    results={results}
                    totalResults={totalResults}
                    translations={translations}
                />
            )}
        </div>
    );
};

Inbox.propTypes = {
    isVrbexNative: PropTypes.bool,
    page: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    pageSize: PropTypes.number,
    systemData: PropTypes.shape({}),
    translations: PropTypes.shape({}),
};

Inbox.defaultProps = {
    isVrbexNative: false,
    page: 1,
    pageSize: 25,
    systemData: {},
    translations: {},
};

// We use 'cache-and-network' here to keep inboxes in sync with the server when a user is archiving/unarchiving
// TODO: We could eliminate all this if we don't care about honoring the inbox/archived route
export default graphql(InboxQuery, {
    options: (ownProps) => ({
        variables: {page: ownProps.page, pageSize: ownProps.pageSize},
        errorPolicy: 'all',
        fetchPolicy: 'cache-and-network',
    }),
})(Inbox);
